import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, ArrowLeft, ArrowRight, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [passwords, setPasswords] = useState({ new: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setError('Lien de réinitialisation invalide');
        return;
      }

      try {
        const response = await fetch(`${API}/auth/verify-reset-token/${token}`);
        
        if (response.ok) {
          setTokenValid(true);
        } else {
          const data = await response.json();
          setError(data.detail || 'Lien de réinitialisation invalide ou expiré');
        }
      } catch (err) {
        setError('Erreur de connexion au serveur');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (passwords.new !== passwords.confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwords.new.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token, 
          password: passwords.new 
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la réinitialisation');
      }

      setSuccess(true);
      toast.success('Mot de passe réinitialisé !');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-fgs-primary animate-spin mx-auto mb-4" />
          <p className="text-fgs-text-muted">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-white mb-4">
              <ShieldCheck className="w-10 h-10 text-fgs-primary" />
              <span className="font-heading text-3xl font-bold">FGS</span>
            </div>
          </div>

          {/* Error Card */}
          <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 md:p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Lien invalide
            </h1>
            
            <p className="text-fgs-text-muted mb-6">
              {error || 'Ce lien de réinitialisation est invalide ou a expiré.'}
            </p>

            <Link to="/forgot-password">
              <Button className="w-full bg-fgs-primary hover:bg-fgs-primary-hover mb-3">
                Demander un nouveau lien
              </Button>
            </Link>

            <Link to="/auth">
              <Button variant="outline" className="w-full border-fgs-border text-white hover:bg-fgs-surface-highlight">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-white mb-4">
              <ShieldCheck className="w-10 h-10 text-fgs-primary" />
              <span className="font-heading text-3xl font-bold">FGS</span>
            </div>
          </div>

          {/* Success Card */}
          <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 md:p-8 text-center">
            <div className="w-16 h-16 bg-fgs-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-fgs-primary" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Mot de passe réinitialisé !
            </h1>
            
            <p className="text-fgs-text-muted mb-6">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>

            <Link to="/auth">
              <Button className="w-full bg-fgs-primary hover:bg-fgs-primary-hover">
                Se connecter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-white mb-4">
            <ShieldCheck className="w-10 h-10 text-fgs-primary" />
            <span className="font-heading text-3xl font-bold">FGS</span>
          </div>
          <p className="text-fgs-text-muted">Créez votre nouveau mot de passe</p>
        </div>

        {/* Form Card */}
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 md:p-8">
          <h1 className="font-heading text-xl font-bold text-white mb-2">
            Nouveau mot de passe
          </h1>
          <p className="text-fgs-text-muted text-sm mb-6">
            Choisissez un mot de passe sécurisé d'au moins 6 caractères.
          </p>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-fgs-accent/10 border border-fgs-accent/30 rounded-lg text-fgs-accent text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-fgs-text-muted text-sm">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fgs-text-muted" />
                <Input
                  type="password"
                  value={passwords.new}
                  onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                  required
                  placeholder="Min. 6 caractères"
                  className="pl-10 bg-fgs-bg border-fgs-border text-white placeholder:text-fgs-text-muted"
                  data-testid="reset-password-new"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-fgs-text-muted text-sm">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fgs-text-muted" />
                <Input
                  type="password"
                  value={passwords.confirm}
                  onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                  required
                  placeholder="Confirmez votre mot de passe"
                  className="pl-10 bg-fgs-bg border-fgs-border text-white placeholder:text-fgs-text-muted"
                  data-testid="reset-password-confirm"
                />
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-fgs-primary hover:bg-fgs-primary-hover h-12 font-semibold"
              data-testid="reset-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Réinitialiser le mot de passe
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <Link 
              to="/auth" 
              className="inline-flex items-center text-sm text-fgs-text-muted hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
